import {PctDouble, Seconds} from '@store/@types/util';
import React, {ReactNode, useState} from 'react';
import formatSecondsShort from 'utils/formatSecondsShort';
import {VolumeSolidIcon} from '@components/icon/VolumeSolidIcon';
import './CallSpeaker.sass';
import {DragStatus, Speaker, SpeakerNumber} from '@store/call-card/CallCard.types';
import {UserAvatar} from '@components/util/UserAvatar';
import IntervalTree from 'node-interval-tree';
import {PlayerProps} from '@components/call-card/Player.utils';
import {MatchLocation} from '@components/call-card/TranscriptMatch.utils';
import {List} from 'immutable';
import {SeekTrack} from './SeekTrack';
import {SetState} from '@store/@types/util/React';
import Tooltip from '@material-ui/core/Tooltip';
import InternalIcon from '@material-ui/icons/Phone';
import ExternalIcon from '@material-ui/icons/RingVolume';

interface CallSpeakerProps extends PlayerProps {
  speaker: Speaker;
  talkTime: Seconds;
  speakerTree: IntervalTree<SpeakerNumber, Seconds>;
  speakerTrack: string | undefined;
  setTimePct: (pct: PctDouble) => void;
  matchLocations: List<MatchLocation>;
  hideInfo: boolean;
  avatarOverride?: ReactNode;
  dragStatus: DragStatus;
  setDragStatus: SetState<DragStatus>;
}

export const CallSpeaker: React.FC<CallSpeakerProps> = ({
  currentTime,
  duration,
  speaker,
  talkTime,
  speakerTree,
  speakerTrack,
  setTimePct,
  matchLocations,
  hideInfo,
  avatarOverride,
  dragStatus,
  setDragStatus,
}) => {
  // Storing trackRef in a state because, with useRef, a bug occurs where
  // new CallSpeaker components rendered at a later time will have `trackRef.current`
  // be null at render time. See COACH-30714 for more detail on the bug.
  const [trackRef, setTrackRef] = useState<HTMLDivElement | null>(null);
  const currentSpeakers = speakerTree.search(currentTime, currentTime);
  const isSpeaking = currentSpeakers.includes(speaker.speakerNumber);
  const isInternal = Boolean(speaker.userId);

  return (
    <div className="call-speaker">
      <div className="speaker">
        {isSpeaking && (
          <VolumeSolidIcon className="speaker-indicator" tooltip="Currently speaking" />
        )}
        {avatarOverride ?? <UserAvatar user={{...speaker, id: speaker.userId}} />}
        {!hideInfo && (
          <div className={`speaker-type ${isInternal ? 'internal' : 'external'}`}>
            <Tooltip title={isInternal ? 'Internal' : 'External'}>
              {speaker.userId ? (
                <InternalIcon className="icon" />
              ) : (
                <ExternalIcon className="icon" />
              )}
            </Tooltip>
          </div>
        )}
      </div>
      <div
        ref={ref => setTrackRef(ref)}
        className="speaker-track"
        role="meter"
        aria-label="Call Progress Bar"
        aria-labelledby="Call Progress Value"
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-valuenow={currentTime}>
        <div className="baseline"></div>
        <svg width="100%" viewBox="0 0 100 10" preserveAspectRatio="none" className="speaking-line">
          <path d={speakerTrack} />
        </svg>
        {matchLocations.map((m, i) => (
          <div
            key={i}
            style={{backgroundColor: m.color, left: m.startTimePct + '%'}}
            className="match-circle"
          />
        ))}
        <div className="speaker-title">{speaker.description}</div>
        <SeekTrack
          trackRef={trackRef}
          setTimePct={setTimePct}
          currentTime={currentTime}
          duration={duration}
          setDragStatus={setDragStatus}
          dragStatus={dragStatus}
          commentModal={false}
        />
      </div>
      <div className="info">
        {!hideInfo && (
          <>
            <span className="speaker-percent">{Math.round((talkTime / duration) * 100)}%</span>
            <span className="speaker-time">{formatSecondsShort(talkTime)}</span>
          </>
        )}
      </div>
    </div>
  );
};
